import React from 'react'

import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import Fab from '@material-ui/core/Fab'
import AddIcon from '@material-ui/icons/Add'
// @src imports
import { Button, DefaultError, InstantSearch, Transition } from 'src/chrome'
import { CampaignPreview, CampaignsTabContent } from 'src/campaigns/components'
import CampaignStore from 'src/campaign_store/containers/CampaignStore'
import { CampaignStoreRoute } from 'src/campaign_store/routes/CampaignStoreRoute'
import { campaignsPageSize, MinimalCampaignFragment } from 'src/graphql'
import suspenseBoundary from 'src/chrome/SuspenseBoundary/suspenseBoundaryHOC'
// relative imports
import { CampaignsRoute } from '../routes/CampaignsRoute'

import styles from './campaigns.module.scss'
import { useActions, useAppcues, useState } from 'src/hooks'
import { MyCampaignsRoute } from '../routes/MyCampaignsRoute'
import { PurchasedCampaignsRoute } from '../routes/PurchasedCampaignsRoute'
import { Text } from '@sendoutcards/quantum-design-ui'
import AnnouncementBanners from 'src/marketing/AnnouncementBanners/AnnouncementBanners'

interface Props {
  route: CampaignsRoute
}

const Campaigns: React.FC<Props> = props => {
  const { route } = props

  const tabStyle = {
    className: styles.className,
    style: { fontSize: 16 },
  }

  const actions = useActions()

  const [search, setSearch] = useState('')
  const [selectedMyCampaignsPage, setSelectedMyCampaignsPage] = useState(1)
  const [selectedPurchasedPage, setSelectedPurchasedPage] = useState(1)

  const [campaignPreviewId, setCampaignPreviewId] = useState<string>()

  const tabContent = route.subroute

  useAppcues('-L9R-PVAOXEbML-UowTv')

  const changeMyCampaignsPage = (page: number) =>
    setSelectedMyCampaignsPage(page > 0 ? page : 1)

  const changePurchasedPage = (page: number) =>
    setSelectedPurchasedPage(page > 0 ? page : 1)

  const previewCampaign = (campaign: MinimalCampaignFragment) =>
    setCampaignPreviewId(campaign.id)

  const closeCampaignPreview = () => setCampaignPreviewId(undefined)

  const campaignsTabContentProps = {
    pageSize: campaignsPageSize,
    setPage: changeMyCampaignsPage,
    onPreview: previewCampaign,
    openCampaign: actions.openCampaign,
    searchTerm: search,
  }

  return (
    <div className={styles.campaignWrapper}>
      <div className={styles.header}>
        <AnnouncementBanners />
        <Text
          type="subtitle"
          color="accent"
          inset={{ top: { xSmall: 'x2', medium: 'x7' }, left: 'x4' }}
        >
          My Campaigns
        </Text>
        <Fab
          id={'create_new_campaign_btn'}
          color="secondary"
          className={styles.addCampaign}
          onClick={actions.openCreateCampaign}
        >
          <AddIcon fontSize="large" />
        </Fab>
      </div>
      <div
        css={{ opacity: tabContent.path === CampaignStoreRoute.path ? 0 : 100 }}
      >
        <InstantSearch
          id={'campaigns_search_bar'}
          onSearch={setSearch}
          className={styles.search}
          autocomplete={'off'}
        />
      </div>
      <Tabs
        value={route.subroute.path}
        style={{
          position: 'relative',
          bottom: 105,
          color: '#fff',
        }}
        onChange={(_, value) => {
          switch (value) {
            case MyCampaignsRoute.path:
              return actions.openCampaigns(MyCampaignsRoute())
            case PurchasedCampaignsRoute.path:
              return actions.openCampaigns(PurchasedCampaignsRoute())
            case CampaignStoreRoute.path:
              return actions.openCampaigns(CampaignStoreRoute())
            default:
              return
          }
        }}
        TabIndicatorProps={{
          style: {
            height: 3,
            width: 145,
            bottom: 3,
            backgroundImage: 'linear-gradient(115deg,#ff5689,#e687ff)',
          },
        }}
      >
        <Tab
          {...tabStyle}
          id={'my_campaigns_tab'}
          label="My Campaigns"
          value={MyCampaignsRoute.path}
        />
        <Tab
          {...tabStyle}
          id={'purchased_campaigns_tab'}
          label={
            <Text type="caption" color="accent">
              Purchased
            </Text>
          }
          value={PurchasedCampaignsRoute.path}
        />
        <Tab
          {...tabStyle}
          id={'store_campaigns_tab'}
          label={
            <Text type="body" color="accent">
              Store
            </Text>
          }
          value={CampaignStoreRoute.path}
        />
      </Tabs>
      {tabContent.path === MyCampaignsRoute.path && (
        <CampaignsTabContent
          {...campaignsTabContentProps}
          page={selectedMyCampaignsPage}
          setPage={changeMyCampaignsPage}
          emptyTitle={
            "Uh oh! It looks like you haven't started any campaigns yet."
          }
          emptySubtitle={'Click + to create one'}
          isShareable={true}
        />
      )}
      {tabContent.path === PurchasedCampaignsRoute.path && (
        <CampaignsTabContent
          {...campaignsTabContentProps}
          page={selectedPurchasedPage}
          setPage={changePurchasedPage}
          emptyTitle={"It looks like you haven't purchased any campaigns yet."}
          emptySubtitle={'Check out the Campaign Store'}
          isShareable={false}
        >
          <Button
            title={'Campaign Store'}
            buttonColor={'pink'}
            onClick={() => actions.openCampaignStore()}
          />
        </CampaignsTabContent>
      )}
      {tabContent.path === CampaignStoreRoute.path && (
        <CampaignStore route={tabContent} />
      )}
      {campaignPreviewId && (
        <CampaignPreview
          campaignId={campaignPreviewId}
          onClose={closeCampaignPreview}
        />
      )}
    </div>
  )
}

export default suspenseBoundary({
  component: Campaigns,
  unresolved: <Transition message={'Loading...'} />,
  failure: DefaultError,
})
